import { Box, Grid2 } from '@mui/material';
import React, { useState, useEffect } from 'react';
import CustomTypography from '@ui-components/Typography/CustomTypography';
import CustomCard from '@ui-components/Card/CustomCard';
import CustomButton from '@ui-components/Button/CustomButton';

//Icon
import VideoPlayIcon from '@assets/images/Video.svg';
import CustomDivider from '@ui-components/Divider/CustomDivider';
import CustomCardContent from '@ui-components/Card/CustomCardContent';
import RelinquiceIcon from '@assets/images/Relinquish-pop.svg';
import ConsultCompleteIcon from '@assets/images/ConsultComplete-pop.svg';
import ChatIcon from '@assets/images/chat.svg';
import AscensionLogo from '@assets/images/ascension_logo.svg';
//Css
import './moredetails.css';
import CustomDialogActions from '@ui-components/Dialog/CustomDialogActions';
import CustomDialogContent from '@ui-components/Dialog/CustomDialogContent';
import CustomIconButton from '@ui-components/IconButton/CustomIconButton';
import CustomDialog from '@ui-components/Dialog/CustomDialog';
import type { SessionDetails } from '../../API/services/consultMoreDetailsService';
import { CallStatusEnum } from '../myconsults/shared/CallStatusEnum';
import { decryptText } from '../healper/decryption';
import { useRelinquishConsult } from '../../API/hooks/consultMoreDetailsHook';
import { useNavigate } from 'react-router-dom';
import { useAcceptConsult } from '../../API/hooks/consultsHook';
import WarningDialog from '../components/AcceptWarningDialog';
import RestartConfirmDialog from '../components/RestartConfirmDialog';
import {
  useStartZoomMeeting,
  useUpdateConsultStatus,
} from '../../shared/zoom/zoomSetup';
import type { AcceptResponse } from '../../API/services/consultsService';

import CustomSnackbar from '@ui-components/Snackbar/CustomSnackbar';
import Interpreter from '../components/Interpreter';
import CustomBadge from '@qler-view/shared_lib/src/shared/lib/ui-components/Badge/CustomBadge';
import CustomLink from '@qler-view/shared_lib/src/shared/lib/ui-components/Link/CustomLink';
import {
  isValidUrl,
  formatFieldValue,
} from '@qler-view/shared_lib/src/reusableFunctions/CommonFunctions';
import { PORTAL_TYPES } from '@qler-view/shared_lib/src/shared/constants/portalType';
import CancelConsultModel, {
  type CancelReasonFacilitySubmitParams,
  type CancelReasonFacilityParams,
} from '@qler-view/shared_lib/src/shared/statusrequests/CancelConsultModel';
import {
  useCancelConsultMetadata,
  useCompleteConsult,
  useCompleteDDLDetails,
  useSaveCancelConsult,
} from '../../shared/statusRequest/StatusRequestSetup';
import CompleteConsultModel from './../../../../../shared_lib/src/shared/statusrequests/CompleteConsultModel';
import { type CompleteConsultParams } from '@qler-view/shared_lib/src/shared/statusrequests/statusRequestService';
import TimeTicker from '@qler-view/shared_lib/src/shared/reusableComponents/TimeTicker';
import TimeDifference from '@qler-view/shared_lib/src/shared/reusableComponents/TimeDifference';

type ConsultPatientDeatilsProps = {
  consultPatientDetails: SessionDetails;
  consultId: number;
  assignedFlag: boolean;
  refetch: () => void;
  onChatToggle?: () => void;
  unreadChatCount?: number;
};

const PatientDetails: React.FC<ConsultPatientDeatilsProps> = ({
  consultPatientDetails,
  consultId,
  assignedFlag,
  refetch,
  onChatToggle,
  unreadChatCount = 0,
}) => {
  const userId = Number.parseInt(localStorage.getItem('userId') ?? '0');
  const [openRelinquish, setOpenRelinquish] = useState(false);
  const handleRelinquishOpen = () => setOpenRelinquish(true);
  const handleRelinquishClose = () => setOpenRelinquish(false);
  const [openComplete, setOpenComplete] = useState(false);
  const [openCancelConsult, setOpenCancelConsult] = useState(false);
  const { completeDDLDetails } = useCompleteDDLDetails(
    PORTAL_TYPES.CLINICAL,
    consultId.toString()
  );

  const [openCallComplete, setOpenCallComplete] = useState(false);
  const handleCompleteCallOpen = () => setOpenCallComplete(true);
  const handleCallCompleteClose = () => setOpenCallComplete(false);

  const [openRestartConfirm, setOpenRestartConfirm] = useState(false);
  const handleRestartOpen = () => setOpenRestartConfirm(true);
  const handleRestartClose = () => setOpenRestartConfirm(false);

  const { CancelConsultMetadata: ConsultCancelMetaData } =
    useCancelConsultMetadata(PORTAL_TYPES.CLINICAL);

  const navigate = useNavigate();

  useEffect(() => {
    if (
      consultPatientDetails?.sessionStatus.toLowerCase() === 'call completed'
    ) {
      localStorage.removeItem(`voyceID-${consultId}`);
    }
  }, [consultPatientDetails?.sessionStatus.toLowerCase()]);

  const onSuccess = () => {
    setMessage('Consult request moved to queue.');
    setOpenToast(true);
    setTimeout(() => {
      setOpenToast(false);
      navigate('/provider/consults', { replace: true });
    }, 3000);
  };

  const onError = (error: unknown) => {
    console.error('Relinquish consult error:', error);
    setMessage('Failed to relinquish consult. Please try again.');
    setOpenToast(true);
    setTimeout(() => {
      setOpenToast(false);
    }, 3000);
  };

  const { mutate: relinquish } = useRelinquishConsult(
    userId,
    onSuccess,
    onError
  );

  const { startZoomMeeting } = useStartZoomMeeting(PORTAL_TYPES.CLINICAL);

  const { mutate: completeConsult } = useCompleteConsult(PORTAL_TYPES.CLINICAL);
  const { mutate: cancelConsult } = useSaveCancelConsult();
  const { mutate: callUpdateConsultStatus } = useUpdateConsultStatus(() => {
    setMessage(
      'The consult request has been moved to the Call Completed status.'
    );
    setOpenToast(true);
    setTimeout(() => {
      setOpenToast(false);
    }, 3000);
  }, userId);

  const handleRelinquishConfirm = (userId: number, consultId: number) => {
    setOpenRelinquish(false);
    relinquish({ userId, consultId });
  };

  const handleCallCompleteConfirm = (
    consultId: number,
    callCompletedFrom: string
  ) => {
    setOpenCallComplete(false);

    const zoomWindowExists = sessionStorage.getItem(`zoomWindow-${consultId}`);
    if (zoomWindowExists) {
      localStorage.setItem(`closeZoom-${consultId}`, Date.now().toString());
      sessionStorage.removeItem(`zoomWindow-${consultId}`);
    }

    callUpdateConsultStatus({
      consultId: consultId,
      sessionStatus: 'Call Completed',
      callCompletedFrom: callCompletedFrom,
    });
  };

  const handleRestartConfirm = () => {
    setOpenRestartConfirm(false);
    startZoomMeeting(
      Number.parseInt(localStorage.getItem('userId') ?? '0'),
      consultId,
      localStorage.getItem('username') ?? 'Clinician'
    );
  };

  const { mutate: accept } = useAcceptConsult(userId, refetch);

  const [alertAccept, setAlertAccept] = useState<{
    message: string;
    title: string;
  } | null>(null);

  const [openToast, setOpenToast] = useState(false);
  const [message, setMessage] = useState('');

  const handleAccept = (userId: number, caseId: number) => {
    accept(
      { userId, caseId },
      {
        onSuccess: (data: AcceptResponse) => {
          if (data?.code === 400) {
            setAlertAccept({
              title: 'Warning',
              message: `This consult request has already been assigned to Dr. ${data.providerFirstName} ${data.providerLastName}`,
            });
          }
        },
        onError: (error: { status: number; message?: string }) => {
          if (error.status === 409) {
            setAlertAccept({
              title: 'Warning',
              message:
                error.message ??
                'You have reached the maximum number of active consults',
            });
          } else {
            setAlertAccept({
              title: 'Error',
              message: 'An error occurred while accepting the consult',
            });
          }
        },
      }
    );
  };
  const handleSubmitCancelReason = (data: CancelReasonFacilityParams) => {
    const mappedTechnicalDifficultiesId =
      [
        data.patientAdmittedId,
        data.noLongerRequiredId,
        data.patientDischargedId,
        data.technicalDifficultiesId,
      ].find((id) => id !== 0) ?? 0;

    const payload: CancelReasonFacilitySubmitParams = {
      cancelReasonId: data.cancelReasonId,
      technicalDifficultiesId: mappedTechnicalDifficultiesId,
      otherReason: data.otherReason,
    };
    cancelConsult(
      {
        userId: userId.toString(),
        portalType: PORTAL_TYPES.CLINICAL,
        consultId,
        ...payload,
      },
      {
        onSuccess: () => {
          refetch();
          setOpenCancelConsult(false);
          setMessage('The consult request has been canceled.');
          setOpenToast(true);
          setTimeout(() => {
            setOpenToast(false);
            navigate('/provider/consults', { replace: true });
          }, 3000);
        },
      }
    );
  };
  const handleSubmitCompleteForm = (complete: CompleteConsultParams) => {
    console.log('complete', complete);
    completeConsult(
      {
        userId: userId,
        consultId: consultId,
        billable: complete.billable,
        diagnosis: complete.diagnosis,
        recommendation: complete.recommendation,
        emCodes: complete.emCodes,
        isSurge: complete.isSurge,
        opsReasonForCompletion: complete.opsReasonForCompletion,
        unitOrDepartment: complete.unitOrDepartment,
        followUp: complete.followUp,
      },
      {
        onSuccess: () => {
          refetch();

          setOpenComplete(false);
          setMessage('The consult request has been marked as completed.');
          setOpenToast(true);
          setTimeout(() => {
            navigate('/provider/consults', { replace: true });
            setOpenToast(false);
          }, 3000);
        },
      }
    );
  };
  const handleClose_SnackBar = () => {
    setOpenToast(false);
  };

  const decryptAndHandleNA = (value: string | undefined | null): string => {
    const decrypted = decryptText(value?.toString() ?? '');
    return decrypted === 'N/A' ? '' : decrypted;
  };

  return (
    <Box>
      {openCancelConsult && (
        <CancelConsultModel
          openCancelConsultPopup={openCancelConsult}
          onCloseCancelConsultPopup={() => setOpenCancelConsult(false)}
          ConsultCancelMetaData={ConsultCancelMetaData ?? undefined}
          onSubmitCancelConsult={(values) => {
            handleSubmitCancelReason(values);
            setOpenCancelConsult(false);
          }}
          portal={PORTAL_TYPES.CLINICAL}
        />
      )}
      <CompleteConsultModel
        openCompletePopup={openComplete}
        onCloseCompletePopup={() => setOpenComplete(false)}
        completeDDLDetails={completeDDLDetails}
        onSubmitCompleteConsult={(values) => {
          handleSubmitCompleteForm(values);
          setOpenComplete(false);
        }}
        portalType={PORTAL_TYPES.CLINICAL}
      />

      {openToast && (
        <CustomSnackbar
          id="moredetails-actions-tost"
          successOrError={'success'}
          open={openToast}
          handleClose={handleClose_SnackBar}
          toastrMessage={message}
        />
      )}

      <Grid2 container>
        <Grid2 size={{ xs: 12 }} className="pb-24">
          <CustomCard id="patient-details" className="width-100">
            <CustomCardContent
              id="patient-details-content"
              className="card-content"
            >
              <Box className="flex align-items-center pb-24">
                <Grid2 size={{ xs: 12, sm: 12, md: 5.9, lg: 5.9, xl: 5.9 }}>
                  <CustomTypography
                    id="patient-name"
                    variant="h4"
                    className="text-align-justify"
                  >
                    {decryptAndHandleNA(consultPatientDetails.patientName)}
                  </CustomTypography>
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 12, md: 6.1, lg: 6.1, xl: 6.1 }}>
                  <Box className="float-right flex align-items-center">
                    {consultPatientDetails?.isExternalConsult !== null &&
                      consultPatientDetails?.isExternalConsult && (
                        <Box className="pr-8">
                          <AscensionLogo />
                        </Box>
                      )}
                    <Box className="pr-16">
                      {consultPatientDetails?.sessionStatus.toLowerCase() ===
                        CallStatusEnum.New ||
                      consultPatientDetails?.sessionStatus.toLowerCase() ===
                        CallStatusEnum.InProgress ? (
                        <Box className="flex clock-text pb-4">
                          <TimeTicker
                            createdOn={consultPatientDetails.createdOn}
                          />
                        </Box>
                      ) : (
                        <Box className="flex clock-text pb-4">
                          <TimeDifference
                            createdOn={consultPatientDetails.createdOn ?? ''}
                            waitEndTime={
                              consultPatientDetails.waitEndTime ?? ''
                            }
                          />
                        </Box>
                      )}
                    </Box>
                    <Box className="mr-12 icon-background-grey">
                      <CustomBadge
                        id={`${consultPatientDetails.id}-chat-notification`}
                        badgeContent={unreadChatCount}
                        color="error"
                        max={99}
                      >
                        <CustomIconButton
                          id="icon-chat"
                          icon={<ChatIcon />}
                          onClick={onChatToggle}
                        ></CustomIconButton>
                      </CustomBadge>
                    </Box>
                    {assignedFlag === false && (
                      <>
                        {consultPatientDetails?.sessionStatus.toLowerCase() !==
                          CallStatusEnum.Completed &&
                          consultPatientDetails?.sessionStatus.toLowerCase() !==
                            CallStatusEnum.Cancelled && (
                            <>
                              {consultPatientDetails?.sessionStatus.toLowerCase() ===
                              CallStatusEnum.New ? (
                                <Box className="pr-12">
                                  <CustomButton
                                    id="btn-accept"
                                    variant="contained"
                                    color="success"
                                    onClick={() =>
                                      handleAccept(userId, consultId)
                                    }
                                  >
                                    Accept
                                  </CustomButton>
                                </Box>
                              ) : (
                                <>
                                  {(consultPatientDetails?.sessionStatus.toLowerCase() ===
                                    CallStatusEnum.InProgress ||
                                    consultPatientDetails?.sessionStatus.toLowerCase() ===
                                      CallStatusEnum.Abandoned ||
                                    (consultPatientDetails?.sessionStatus.toLowerCase() ===
                                      CallStatusEnum.CallStarted &&
                                      consultPatientDetails?.isWebZoom ===
                                        false &&
                                      consultPatientDetails?.isDesktopZoom ===
                                        false) ||
                                    (consultPatientDetails?.sessionStatus.toLowerCase() ===
                                      CallStatusEnum.CallStarted &&
                                      consultPatientDetails?.isExternalConsult !==
                                        null &&
                                      consultPatientDetails?.isExternalConsult)) && (
                                    <Box className="pr-12">
                                      <CustomButton
                                        id="btn-relinquish"
                                        variant="outlined"
                                        color="error"
                                        onClick={handleRelinquishOpen}
                                      >
                                        Relinquish
                                      </CustomButton>

                                      <CustomDialog
                                        id="dialogWarning"
                                        open={openRelinquish}
                                        onClose={handleRelinquishClose}
                                      >
                                        <CustomDialogContent className="dialog-width">
                                          <Box className="pb-12">
                                            <RelinquiceIcon />
                                          </Box>
                                          <Box className="pb-4">
                                            <CustomTypography
                                              id="relinquish-title-popup"
                                              variant="subtitle1"
                                            >
                                              Confirm Action
                                            </CustomTypography>
                                          </Box>
                                          <Box>
                                            <CustomTypography
                                              id="relinquish-title-popup"
                                              variant="body3"
                                            >
                                              Relinquishing the consult request
                                              returns it to the available
                                              consults queue. Do you want to
                                              proceed?
                                            </CustomTypography>
                                          </Box>
                                        </CustomDialogContent>

                                        <CustomDialogActions>
                                          <Grid2 container>
                                            <Grid2 className="mr-8">
                                              <CustomButton
                                                id="btnYes"
                                                color="error"
                                                variant="outlined"
                                                onClick={handleRelinquishClose}
                                              >
                                                Cancel
                                              </CustomButton>
                                            </Grid2>
                                            <Grid2>
                                              <CustomButton
                                                id="btnNo"
                                                color="primary"
                                                variant="contained"
                                                onClick={() =>
                                                  handleRelinquishConfirm(
                                                    userId,
                                                    consultId
                                                  )
                                                }
                                              >
                                                Confirm
                                              </CustomButton>
                                            </Grid2>
                                          </Grid2>
                                        </CustomDialogActions>
                                      </CustomDialog>
                                    </Box>
                                  )}
                                  {(consultPatientDetails?.sessionStatus.toLowerCase() ===
                                    CallStatusEnum.InProgress ||
                                    consultPatientDetails?.sessionStatus.toLowerCase() ===
                                      CallStatusEnum.CallStarted) && (
                                    <Box className="pr-12">
                                      <CustomButton
                                        id="btn-start"
                                        variant="contained"
                                        color="primary"
                                        startIcon={<VideoPlayIcon />}
                                        onClick={() => {
                                          if (
                                            consultPatientDetails?.sessionStatus.toLowerCase() ===
                                            CallStatusEnum.CallStarted
                                          ) {
                                            handleRestartOpen();
                                          } else {
                                            startZoomMeeting(
                                              Number.parseInt(
                                                localStorage.getItem(
                                                  'userId'
                                                ) ?? '0'
                                              ),
                                              consultId,
                                              localStorage.getItem(
                                                'username'
                                              ) ?? 'Clinician'
                                            );
                                          }
                                        }}
                                      >
                                        {consultPatientDetails?.sessionStatus.toLowerCase() ===
                                        CallStatusEnum.CallStarted
                                          ? 'Restart'
                                          : 'Start'}
                                      </CustomButton>

                                      <RestartConfirmDialog
                                        open={openRestartConfirm}
                                        onClose={handleRestartClose}
                                        onConfirm={handleRestartConfirm}
                                      />
                                    </Box>
                                  )}
                                  {consultPatientDetails?.sessionStatus.toLowerCase() ===
                                    CallStatusEnum.CallStarted &&
                                    (consultPatientDetails?.isWebZoom ||
                                      consultPatientDetails?.isDesktopZoom) &&
                                    localStorage.getItem(
                                      `meetingUrl-${consultId}`
                                    ) && (
                                      <Box className="pr-12">
                                        <Interpreter
                                          ConsultId={consultId}
                                          callstatus={consultPatientDetails?.sessionStatus.toLowerCase()}
                                        />
                                      </Box>
                                    )}
                                </>
                              )}

                              {consultPatientDetails?.sessionStatus.toLowerCase() ===
                                CallStatusEnum.CallCompleted && (
                                <Box className="pr-12">
                                  <CustomButton
                                    id="btn-complete"
                                    variant="contained"
                                    color="success"
                                    onClick={() => {
                                      setOpenComplete(true);
                                    }}
                                  >
                                    Complete
                                  </CustomButton>
                                </Box>
                              )}

                              {consultPatientDetails?.sessionStatus.toLowerCase() ===
                                CallStatusEnum.CallStarted && (
                                <Box className="pr-12">
                                  <CustomButton
                                    id="btn-CallComplete"
                                    variant="contained"
                                    color="success"
                                    onClick={handleCompleteCallOpen}
                                  >
                                    Complete Call
                                  </CustomButton>

                                  <CustomDialog
                                    id="dialogWarning"
                                    open={openCallComplete}
                                    onClose={handleCallCompleteClose}
                                  >
                                    <CustomDialogContent className="dialog-width">
                                      <Box className="pb-12">
                                        <ConsultCompleteIcon />
                                      </Box>
                                      <Box className="pb-4">
                                        <CustomTypography
                                          id="CallComplete-title-popup"
                                          variant="subtitle1"
                                        >
                                          Complete Call?
                                        </CustomTypography>
                                      </Box>
                                      <Box>
                                        {consultPatientDetails?.isWebZoom ===
                                          true ||
                                        consultPatientDetails?.isDesktopZoom ===
                                          true ? (
                                          <>
                                            <CustomTypography
                                              id="relinquish-title-popup"
                                              variant="body3"
                                              className="pb-4"
                                            >
                                              Are you sure you want to mark the
                                              call as completed?
                                            </CustomTypography>
                                            {consultPatientDetails?.isExternalConsult !==
                                              true && (
                                              <CustomTypography
                                                id="relinquish-title-popup"
                                                variant="body3"
                                              >
                                                If you are currently on an
                                                ongoing call, clicking Confirm
                                                will end the call. Do you want
                                                to proceed?
                                              </CustomTypography>
                                            )}
                                          </>
                                        ) : (
                                          <CustomTypography
                                            id="relinquish-title-popup"
                                            variant="body3"
                                          >
                                            Are you sure you want to mark the
                                            call as completed?
                                          </CustomTypography>
                                        )}
                                      </Box>
                                    </CustomDialogContent>

                                    <CustomDialogActions>
                                      <Grid2 container>
                                        <Grid2 className="mr-8">
                                          <CustomButton
                                            id="btnYes"
                                            color="error"
                                            variant="outlined"
                                            onClick={handleCallCompleteClose}
                                          >
                                            Cancel
                                          </CustomButton>
                                        </Grid2>
                                        <Grid2>
                                          <CustomButton
                                            id="btnNo"
                                            color="primary"
                                            variant="contained"
                                            onClick={() =>
                                              handleCallCompleteConfirm(
                                                consultId,
                                                'app'
                                              )
                                            }
                                          >
                                            Confirm
                                          </CustomButton>
                                        </Grid2>
                                      </Grid2>
                                    </CustomDialogActions>
                                  </CustomDialog>
                                </Box>
                              )}
                              {(consultPatientDetails?.sessionStatus.toLowerCase() ===
                                CallStatusEnum.InProgress ||
                                consultPatientDetails?.sessionStatus.toLowerCase() ===
                                  CallStatusEnum.Cancelled ||
                                consultPatientDetails?.sessionStatus.toLowerCase() ===
                                  CallStatusEnum.CallStarted ||
                                consultPatientDetails?.sessionStatus.toLowerCase() ===
                                  CallStatusEnum.CallCompleted) && (
                                <Box className="pr-12">
                                  <CustomButton
                                    id="btn-complete"
                                    variant="outlined"
                                    color="error"
                                    onClick={() => setOpenCancelConsult(true)}
                                  >
                                    Cancel
                                  </CustomButton>
                                </Box>
                              )}
                            </>
                          )}
                      </>
                    )}
                  </Box>
                </Grid2>
              </Box>

              <CustomDivider />
              <Box className="pt-24">
                <Box>
                  <Grid2 container spacing={0.5} className="text-align-left">
                    {consultPatientDetails?.sessionFields.map(
                      (field, index) => (
                        <Grid2
                          key={`${field.fieldLabel}-${index}`}
                          size={{ xs: 12, sm: 12, md: 4, lg: 2.4, xl: 2.4 }}
                          className="text-left pb-24"
                        >
                          <Box>
                            <CustomTypography
                              id={`lbl-${field.fieldLabel}-${index}-label`}
                              variant="body2"
                              color="textSecondary"
                              className="pb-4"
                            >
                              {field.fieldLabel}
                            </CustomTypography>
                            <CustomTypography
                              id={`lbl-${field.fieldLabel}-${index}-value`}
                              variant="body3"
                            >
                              {formatFieldValue(field.fieldValue, {
                                isDateOfBirth:
                                  field.fieldLabel === 'Date Of Birth',
                              })}
                            </CustomTypography>
                          </Box>
                        </Grid2>
                      )
                    )}
                  </Grid2>
                </Box>
              </Box>
            </CustomCardContent>
          </CustomCard>
        </Grid2>
      </Grid2>
      <Grid2 container>
        <Grid2 size={{ xs: 12 }} className="pb-24">
          <CustomCard
            id="facility-additional-session-fields"
            className="width-100"
          >
            <CustomCardContent
              id="facility-additional-consult-fields-content"
              className="card-content"
            >
              <Box className="flex align-items-center pb-24">
                <Grid2 size={{ xs: 12, sm: 12, md: 5.9, lg: 5.9, xl: 5.9 }}>
                  <CustomTypography
                    id="additional-consult-fields-title"
                    variant="h4"
                    className="text-align-justify"
                  >
                    Additional Information
                  </CustomTypography>
                </Grid2>
              </Box>
              <CustomDivider />
              <Box className="pt-24">
                <Box>
                  <Grid2 container spacing={0.5} className="text-align-left">
                    {consultPatientDetails &&
                    consultPatientDetails.additionalSessionFields.length > 0 ? (
                      consultPatientDetails.additionalSessionFields.map(
                        (field, index) => (
                          <Grid2
                            key={`${field.fieldLabel}-${index}`}
                            size={{ xs: 12, sm: 12, md: 4, lg: 2.4, xl: 2.4 }}
                            className="text-left pb-24"
                          >
                            <Box>
                              <CustomTypography
                                id={`lbl-${field.fieldLabel}-label`}
                                variant="body2"
                                color="textSecondary"
                                className="pb-4"
                              >
                                {field.fieldLabel}
                              </CustomTypography>
                              {isValidUrl(field.fieldValue) ? (
                                <CustomLink
                                  id={`lbl-${field.fieldLabel}-value`}
                                  variant="body3"
                                  className="content-align"
                                  href={
                                    field.fieldValue?.startsWith('http://') ||
                                    field.fieldValue?.startsWith('https://')
                                      ? field.fieldValue
                                      : `https://${field.fieldValue}`
                                  }
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  underline="hover"
                                  color="primary"
                                >
                                  {field.fieldValue}
                                </CustomLink>
                              ) : (
                                <CustomTypography
                                  id={`lbl-${field.fieldLabel}-value`}
                                  variant="body3"
                                  className="content-align"
                                >
                                  {formatFieldValue(field.fieldValue)}
                                </CustomTypography>
                              )}
                            </Box>
                          </Grid2>
                        )
                      )
                    ) : (
                      <Grid2
                        size={{ xs: 12, sm: 12, md: 4, lg: 2.4, xl: 2.4 }}
                        className="button-width"
                      >
                        <Box className="text-align-center no-consults-data">
                          <CustomTypography
                            variant="body2"
                            color="textDisabled"
                            id="noadditionalFieldsAvailable"
                          >
                            No Additional Information Available
                          </CustomTypography>
                        </Box>
                      </Grid2>
                    )}
                  </Grid2>
                </Box>
              </Box>
            </CustomCardContent>
          </CustomCard>
        </Grid2>
      </Grid2>
      {alertAccept && (
        <WarningDialog
          alertAccept={alertAccept}
          onClose={() => setAlertAccept(null)}
          buttonText="Close"
        />
      )}
    </Box>
  );
};

export default PatientDetails;
